import { Block } from '../block';
import { Bounding } from '../../../core/models/bounding';
import { Element } from '../../../equipment/models/elements/element';
import { EquipmentElements } from '../../../equipment/models/elements/equipmentElements';
import { Extension, ExtensionType, RoundingParams } from '../../../equipment/models/elements/extension';
import { ICanPutProductsResult } from '../canPutProductsResult';
import { Point } from '../../../core/models/point';
import { ProductGroup } from '../product-group';
import { Rectangle } from '../../../equipment/models/figures/rectangle';

import { Utils } from '../../../core/services/utils.service';
import * as jsts from 'jsts';

const MAGNETIZE_COEFFICIENT: number = 0.01;

type MagnetizeAction = {
	type: 'corner-corner' | 'corner-side' | 'side-corner',
	corner: Point,
	nearestCorner?: Point,
	side?: [Point, Point]
}

export class PlaneElement extends Element {

	get allowedAutoLayout(): boolean{
		return !this.twoSided;
	}

	get layoutBoundingY(): Bounding {
		const bounding: Bounding = this.getBoundingY();

		return {
			min: bounding.max,
			max: bounding.max
		}
	}

	get products(): Array<Array<ProductGroup>> {
		const reverse: number = this.stand.alignment === 'right' ? -1 : 1;
		const products: Array<ProductGroup> = super.sortedProducts;

		if (this.twoSided) {
			const center: number = this.center.z;
			const frontProducts: Array<ProductGroup> = products.filter((pg: ProductGroup) => pg.begin.z >= center);
			const backProducts: Array<ProductGroup> = products.filter((pg: ProductGroup) => pg.begin.z < center);

			return [
				...Utils
					.groupByChildProperty(frontProducts, 'begin', 'x')
					.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => reverse * (a[0].begin.x - b[0].begin.x)),
				...Utils
					.groupByChildProperty(backProducts, 'begin', 'x')
					.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => reverse * (a[0].begin.x - b[0].begin.x))
			];
		}

		return Utils
			.groupByChildProperty(products, 'begin', 'x')
			.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => reverse * (a[0].begin.x - b[0].begin.x));
	}

	get sortedProducts(): Array<ProductGroup> {
		if (this.twoSided) {
			const frontProducts: Array<ProductGroup> = super.sortedProducts
				.filter((pg: ProductGroup) => pg.begin.z >= this.center.z);
			const backProducts: Array<ProductGroup> = super.sortedProducts
				.filter((pg: ProductGroup) => pg.begin.z < this.center.z);

			return [
				...frontProducts.sort((a: ProductGroup, b: ProductGroup) => {
					return a.begin.x - b.begin.x || a.begin.y - b.begin.y || b.begin.z - a.begin.z;
				}),
				...backProducts.sort((a: ProductGroup, b: ProductGroup) => {
					return b.begin.x - a.begin.x || a.begin.y - b.begin.y || a.begin.z - b.begin.z;
				})
			];
		} else {
			return super.sortedProducts.sort((a: ProductGroup, b: ProductGroup) => {
				return a.begin.x - b.begin.x || a.begin.y - b.begin.y || b.begin.z - a.begin.z;
			});
		}
	}

	protected get twoSided(): boolean {
		const baseWithCentralPillar: boolean = this.relatedElements
			.some((el: Element) => el.parent.is(EquipmentElements.CentralPillar));

		return this.depend?.parent.is(EquipmentElements.CentralPillar) || baseWithCentralPillar;
	}

	constructor(element: Element) {
		super(element.parent, element.shapes, element.extensions, element.depend, element.id, element.idx);
	}

	public addProduct(pg: ProductGroup): void {
		pg.element = this;

		let anchorZCoordinate: number = this.anchorZCoordinate;
		let deltaZ: number = Utils.round(this.getBoundingZ().max - pg.begin.z);

		if (this.twoSided && (pg.begin.z < this.center.z)) anchorZCoordinate = this.layoutBoundingZ.min;

		if (anchorZCoordinate === this.layoutBoundingZ.min) {
			deltaZ = Utils.round(this.getBoundingZ().min - pg.begin.z);
		}

		pg.begin = {
			x: pg.begin.x,
			y: this.anchorYCoordinate,
			z: Utils.round(anchorZCoordinate - deltaZ)
		};
	}

	protected arbitraryPart (point: Point): boolean {
		if (!this.isExtensionActive(ExtensionType.Rounding)) return false;

		const rounding: Extension<RoundingParams> = this.getExtension<RoundingParams>(ExtensionType.Rounding);
		const leftSideRadius: number = Utils.convertPercentToRadius(rounding.params.leftSideRadius, this);
		const rightSideRadius: number = Utils.convertPercentToRadius(rounding.params.rightSideRadius, this);

		if (point.x < Utils.round(this.getBoundingX().min + leftSideRadius)) return true;
		if (point.x > Utils.round(this.getBoundingX().max - rightSideRadius)) return true;

		return false;
	}

	public canMoveProductGroup(productGroup: ProductGroup, x?: number, y?: number, z?: number): boolean {
		let canMove: boolean = false;
		const elementBoundingX: Bounding = this.layoutBoundingX;
		const elementBoundingZ: Bounding = this.layoutBoundingZ;
		const productBoundingX: Bounding = productGroup.getBoundingX();
		const productBoundingZ: Bounding = productGroup.getBoundingZ();

		if (x && x < 0 && elementBoundingX.min <= Utils.round(productBoundingX.min + x)) canMove = true;
		if (x && x > 0 && elementBoundingX.max >= Utils.round(productBoundingX.max + x)) canMove = true;
		if (z && z < 0 && elementBoundingZ.min <= Utils.round(productBoundingZ.min + z)) canMove = true;
		if (z && z > 0 && elementBoundingZ.max >= Utils.round(productBoundingZ.max + z)) canMove = true;

		return canMove;
	}

	public canPutProductGroups(products: Array<ProductGroup>, point: Point): ICanPutProductsResult {
		if (products.some((pg: ProductGroup) => pg.isLooseForm)) {
			return {
				success: false,
				message: {
					messageText: 'WEIGHT_PRODUCT_ON_SHELF_ERROR',
					translateParams: {}
				}
			};
		}

		const block: Block = this.stand.findBlock(point.x, point.y, products[0].Width);

		for (let product of products) {
			if (block && (product.block != block.id || !block.id)) {
				if (!block.isProductSuitable(product.product)) {
					return {
						success: false,
						message: {
							messageText: 'BLOCK_PRODUCT_ERROR',
							translateParams: { blockName: block.name }
						}
					};
				}
			}
		}

		return {
			success: true,
			message: {
				messageText: '',
				translateParams: {}
			}
		}
	}

	public canRotateProductGroup(productGroup: ProductGroup): boolean {
		if (productGroup.dependProduct) return false;
		if (productGroup.isLooseForm) return false;

		return this.stand.arbitraryLayout || this.arbitraryPart(productGroup.detailedRotationPoint);
	}

	public putProductGroups(products: Array<ProductGroup>, list: Array<Array<ProductGroup>>, point: Point): void {
		this.removePreviousProducts(products);

		const firstProductAutoDepth: boolean = products[0].autoDepth;
		const lowerProduct: ProductGroup = this.findLowerProduct(products, list, point);

		products[0].dependProduct = lowerProduct == products[0] ? null : lowerProduct;

		if (!this.stand.arbitraryLayout && !this.arbitraryPart(point)) {
			const priorProduct: ProductGroup = this.findPriorProduct(products, list, point);
			if (priorProduct) {
				products.forEach((pg: ProductGroup) => pg.angle = priorProduct.angle);
				this.adjustPointToPriorProduct(priorProduct, products, point);
			}
		} else {
			this.magnetizePoint(products, list, point);
		}

		products
			.filter((pg: ProductGroup) => !pg.dependProduct)
			.sort((a: ProductGroup, b: ProductGroup) => b.getBoundingZ().max - a.getBoundingZ().max)
			.forEach((pg: ProductGroup, index: number, list: Array<ProductGroup>) => {
				const autoDepth: boolean = pg == products[0] ? firstProductAutoDepth : pg.autoDepth;
				const facesQty: number = pg.FacesQty;

				pg.Element = this;
				pg.begin.x = point.x;
				pg.begin.z = index ? list[index - 1].getBoundingZ().min : point.z;

				if (!this.stand.arbitraryLayout) {
					const maxPlacementDepth: number = this.getMaxPlacementDepth(pg);
					if (autoDepth || maxPlacementDepth < pg.placementDepth) pg.placementDepth = maxPlacementDepth;
				}

				pg.FacesQty = facesQty;

				this.stand.moveProductToTheEnd(pg);
			});

		products
			.filter((pg: ProductGroup) => pg.dependProduct)
			.sort((a: ProductGroup, b: ProductGroup) => a.begin.y - b.begin.y)
			.forEach((pg: ProductGroup) => {
				const autoDepth: boolean = pg == products[0] ? firstProductAutoDepth : pg.autoDepth;
				const facesQty: number = pg.FacesQty;

				pg.Element = pg.dependProduct.element;
				pg.begin.x = pg.dependProduct.begin.x;
				pg.begin.y = pg.dependProduct.getBoundingY().max;
				pg.begin.z = pg.dependProduct.begin.z;

				if (!this.stand.arbitraryLayout) {
					const maxPlacementDepth: number = pg.dependProduct.element.getMaxPlacementDepth(pg);
					if (autoDepth || maxPlacementDepth < pg.placementDepth) pg.placementDepth = maxPlacementDepth;
				}

				pg.FacesQty = facesQty;

				this.stand.moveProductToTheEnd(pg);
			});
	}

	public shiftProductGroups(activeGroups: Array<Array<ProductGroup>>, excludeProducts: Array<ProductGroup> = []): void {
		if (!this.stand.arbitraryLayout && !this.arbitraryPart(activeGroups[0][0].begin)) {
			const checkPoint: number = Utils.min(activeGroups.map((group: Array<ProductGroup>) => group[0].begin.x));
			const currentSegment: Bounding = (this.stand.alignment !== 'default' ? this.getBlockSegments : this.getSiblingSegments())
				.find((segment: Bounding) => segment.min <= checkPoint && segment.max > checkPoint);

			if (!currentSegment) return;

			let products: Array<Array<ProductGroup>> = [this, ...this.getSiblingElements]
					.reduce((products: Array<Array<ProductGroup>>, el: Element) => [...products, ...el.products], [])
					.map((group: Array<ProductGroup>) => group.filter((pg: ProductGroup) => !excludeProducts.includes(pg)))
					.filter((group: Array<ProductGroup>) => group.length);

			products = this.filterArbitraryProducts(products);

			if (this.twoSided) {
				const zPoint: number = Utils.min(activeGroups.map((group: Array<ProductGroup>) => group[0].begin.z));
				const centerPoint: number = this.center.z;

				if (zPoint < centerPoint) {
					products = products.filter((group: Array<ProductGroup>) => group[0].begin.z < centerPoint);
				} else {
					products = products.filter((group: Array<ProductGroup>) => group[0].begin.z > centerPoint);
				}
			}

			const leftProducts: Array<Array<ProductGroup>> = products
					.filter((group: Array<ProductGroup>) => {
						return group[0].begin.x < checkPoint && group[0].begin.x >= currentSegment.min;
					})
					.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => b[0].begin.x - a[0].begin.x);

			const rightProducts: Array<Array<ProductGroup>> = products
					.filter((group: Array<ProductGroup>) => {
						return group[0].begin.x >= checkPoint && group[0].begin.x < currentSegment.max;
					})
					.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => a[0].begin.x - b[0].begin.x);

			const startPoint: number = Utils.min(activeGroups.map((group: Array<ProductGroup>) => Element.calculateGroupMin(group)));
			this.shiftLeft(startPoint, leftProducts);

			const endPoint: number = Utils.max(activeGroups.map((group: Array<ProductGroup>) => Element.calculateGroupMax(group)));
			this.shiftRight(endPoint, rightProducts);

			if (this.stand.alignment === 'right') {
				const productsToShift: Array<Array<ProductGroup>> = [...leftProducts, ...activeGroups, ...rightProducts]
					.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => a[0].begin.x - b[0].begin.x);
				this.shiftRight(currentSegment.min, productsToShift);
				const productsToShiftLeft: Array<Array<ProductGroup>> = [
					...productsToShift
						.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => b[0].begin.x - a[0].begin.x),
					...products
						.filter((group: Array<ProductGroup>) => {
							const groupMin: number = Element.calculateGroupMin(group);
							return groupMin < currentSegment.min && !leftProducts.some((pg: Array<ProductGroup>) => pg.includes(group[0]));
						})
						.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => b[0].begin.x - a[0].begin.x)
				];
				this.shiftLeft(currentSegment.max, productsToShiftLeft);
			} else {
				const productsToShift: Array<Array<ProductGroup>> = [...rightProducts, ...activeGroups, ...leftProducts]
					.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => b[0].begin.x - a[0].begin.x);
				this.shiftLeft(currentSegment.max, productsToShift);
				const productsToShiftRight: Array<Array<ProductGroup>> = [
					...productsToShift
						.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => a[0].begin.x - b[0].begin.x),
					...products
						.filter((group: Array<ProductGroup>) => {
							const groupMax: number = Element.calculateGroupMax(group);
							return groupMax > currentSegment.max && !rightProducts.some((pg: Array<ProductGroup>) => pg.includes(group[0]));
						})
						.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => a[0].begin.x - b[0].begin.x)
				];
				this.shiftRight(currentSegment.min, productsToShiftRight);
			}
		}
	}

	private shiftLeft(startPoint: number, groups: Array<Array<ProductGroup>>): void {
		let index: number = 0;
		while (index < groups.length && Element.calculateGroupMax(groups[index]) > startPoint) {
			let finalPoint: number = Utils.round(startPoint - Element.calculateGroupWidth(groups[index]));

			if (this.twoSided && groups[index][0].begin.z < this.center.z) {
				finalPoint = startPoint;
			}

			groups[index].forEach((pg: ProductGroup) => pg.begin.x = finalPoint);
			startPoint = Element.calculateGroupMin(groups[index]);
			index++;
		}
	};

	private shiftRight(endPoint: number, groups: Array<Array<ProductGroup>>): void {
		let index: number = 0;
		while (index < groups.length && Element.calculateGroupMin(groups[index]) < endPoint) {
			let finalPoint: number = endPoint;

			if (this.twoSided && groups[index][0].begin.z < this.center.z) {
				finalPoint = Utils.round(endPoint + Element.calculateGroupWidth(groups[index]));
			}

			groups[index].forEach((pg: ProductGroup) => pg.begin.x = finalPoint);
			endPoint = Element.calculateGroupMax(groups[index]);
			index++;
		}
	};

	protected findSameXProducts(exclude: ProductGroup[], boundingX: Bounding): Array<Array<ProductGroup>> {
		const xBounding: Bounding = boundingX || {
			min: Element.calculateGroupMin(exclude),
			max: Element.calculateGroupMax(exclude)
		};

		return this.getSiblingElements
			.map((el: Element) => el.products)
			.flat(1)
			.concat(this.products)
			.filter((group: Array<ProductGroup>) => {
				return !(
					xBounding.max < Element.calculateGroupMin(group) ||
					xBounding.min > Element.calculateGroupMax(group)
				);
			})
			.map((group: Array<ProductGroup>) => group.filter((pg: ProductGroup) => !exclude.includes(pg)))
			.filter((group: Array<ProductGroup>) => group.length)
			.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => a[0].begin.x - b[0].begin.x);
	}

	protected removePreviousProducts(group: Array<ProductGroup>): void {}

	protected findLowerProduct(products: Array<ProductGroup>, list: Array<Array<ProductGroup>>, point: Point): ProductGroup {
		if (list.length == 1 && list[0].every((pg: ProductGroup) => pg.begin.z == list[0][0].begin.z)) {
			const correctY: number = point.y + (this.parent.type == 'AngleShelf' ? this.Height : 0);
			return this.getDroppedProduct(point.x, correctY, point.z, list.flat(), products[0]);
		}

		return null;
	}

	protected findPriorProduct(group: Array<ProductGroup>, list: Array<Array<ProductGroup>>, point: Point): ProductGroup {
		return null;
	}

	protected adjustPointToPriorProduct(priorPg: ProductGroup, group: Array<ProductGroup>, point: Point): void {}

	protected magnetizePoint(group: Array<ProductGroup>, list: Array<Array<ProductGroup>>, point: Point) {
		const excludeProducts: Array<ProductGroup> = Utils.to1DArray(list);
		const elementProducts: Array<ProductGroup> = this.stand.products
			.filter((pg: ProductGroup) => pg.element == this && !excludeProducts.includes(pg));

		// calculation of how much the product has shifted
		const deltaX: number = point.x - group[0].begin.x;
		const deltaZ: number = point.z - group[0].begin.z;

		// calculation of product corners after shifted
		const productCorners: Array<Point> = group[0].zCoordinates.map((corner: Point) => {
			return new jsts.geom.Coordinate(
				Utils.round(corner.x + deltaX),
				Utils.round(corner.y + deltaZ)
			);
		});
		// calculation of product sides after shifted
		const productSides: Array<[Point, Point]> = Utils.getItemSides(productCorners);

		// calculation of the angles of all products and elements to which can magnetize
		const itemsCorners: Array<Array<Point>> = this.getItemsZCoordinates(elementProducts, group[0]);

		let nearestDistanceToSides: number = Infinity;
		let nearestDistanceToCorners: number = Infinity;
		let magnetizeAction: MagnetizeAction  = null;

		for (let itemCorners of itemsCorners) {
			// looking for the nearest corners to product corners
			for (let productCorner of productCorners) {
				for (let itemCorner of itemCorners) {
					const distance: number = Utils.distance(itemCorner.x, itemCorner.y, productCorner.x, productCorner.y);
					if (distance <= MAGNETIZE_COEFFICIENT && distance <= nearestDistanceToCorners) {
						nearestDistanceToCorners = distance;
						magnetizeAction = {
							type: 'corner-corner',
							corner: productCorner,
							nearestCorner: itemCorner
						};
					}
				}   
			}

			if (nearestDistanceToCorners === Infinity || !Utils.isRightAngle(group[0].angle)) {
                if (!Utils.isRightAngle(group[0].angle)) nearestDistanceToSides = nearestDistanceToCorners;
				// looking for the nearest corners to product sides
				for (let productSide of productSides) {
					for (let itemCorner of itemCorners) {
						const distance: number = Utils.distancePointToSegment(itemCorner, productSide);
						if ( distance <= MAGNETIZE_COEFFICIENT && distance <= nearestDistanceToSides) {
							nearestDistanceToSides = distance;
							magnetizeAction = {
								type: 'side-corner',
								corner: itemCorner,
								side: productSide
							};
						}
					}
				}

				// looking for the nearest side to product corners
				const itemSides: Array<[Point, Point]> = Utils.getItemSides(itemCorners);
				for (let productCorner of productCorners) {
					for (let itemSide of itemSides) {
						const distance: number = Utils.distancePointToSegment(productCorner, itemSide);
						if (distance <= MAGNETIZE_COEFFICIENT && distance <= nearestDistanceToSides) {
							nearestDistanceToSides = distance;
							magnetizeAction = {
								type: 'corner-side',
								corner: productCorner,
								side: itemSide
							};
						}
					}
				}
			}
		}

		if (magnetizeAction) {
			const result: Point = this.applyMagnetize(magnetizeAction);
			if (result.x) point.x = Utils.round(point.x + result.x);
			if (result.z && (group[0].element.shapes[0] as Rectangle).representation === 'top') {
				point.z = Utils.round(point.z + result.z);
			}
		}
	}

	protected applyMagnetize(action: MagnetizeAction): Point {
		switch (action.type) {
			case 'corner-corner': {
				return {
					x: action.nearestCorner.x - action.corner.x,
					y: 0,
					z: action.nearestCorner.y - action.corner.y
				}
			}
			case 'corner-side':
			case 'side-corner': {
				// Get the coordinates of the start and end points of the side, and the corner to be magnetized
				const [sideStart, sideEnd] = action.side;
				const corner: Point = action.corner;

				// Calculate the squared length of the side for normalization
				const sideLengthSquared: number = Math.pow(sideEnd.x - sideStart.x, 2) + Math.pow(sideEnd.y - sideStart.y, 2);

				// Calculate the projection factor 't', which determines the projection of the corner onto the side segment
				let projectionFactor: number = ((corner.x - sideStart.x) * (sideEnd.x - sideStart.x) +
					(corner.y - sideStart.y) * (sideEnd.y - sideStart.y)) / sideLengthSquared;

				// Clamp 't' to the range [0, 1] to ensure the projection remains within the bounds of the side
				projectionFactor = Math.max(0, Math.min(1, projectionFactor));

				// Calculate the new coordinates for the corner, projecting it onto the side
				let offsetX = sideStart.x + projectionFactor * (sideEnd.x - sideStart.x) - corner.x;
				let offsetZ = sideStart.y + projectionFactor * (sideEnd.y - sideStart.y) - corner.y;

				// If the action type is 'side-corner', invert the offsets on both axes
				if (action.type === 'side-corner') {
					offsetX = -offsetX;
					offsetZ = -offsetZ;
				}

				// Return the new coordinates for the magnetization adjustment
				return {
					x: offsetX,
					y: 0,
					z: offsetZ
				};
			}
		}
	}

	protected getItemsZCoordinates(productGroups: Array<ProductGroup>, product: ProductGroup): Array<Array<Point>> {
		const productBoundingX: Bounding = product.getBoundingX();
		const productBoundingZ: Bounding = product.getBoundingZ();

		return productGroups
			.filter((pg: ProductGroup) => {
				const boundingX: Bounding = pg.getBoundingX();
				const boundingZ: Bounding = pg.getBoundingZ();
				const isWithinX: boolean = boundingX.max >= productBoundingX.min && boundingX.min <= productBoundingX.max;
				const isWithinZ:boolean = boundingZ.max >= productBoundingZ.min && boundingZ.min <= productBoundingZ.max;
				return isWithinX || isWithinZ;
			})
			.map((pg: ProductGroup) => pg.zCoordinates);
	}

	protected intersectsProduct(pg: ProductGroup): boolean {
		if (pg.dependProduct) return false;

		const products: Array<ProductGroup> = [this, ...this.getSiblingElements]
			.reduce((groups: Array<Array<ProductGroup>>, el: PlaneElement) => [...groups, ...el.products], [])
			.flat()
			.filter((product: ProductGroup) => product != pg && !product.dependProduct);

		return products.some((p: ProductGroup) => pg.zPolygon.intersects(p.zPolygon) && !pg.zPolygon.touches(p.zPolygon));
	}

	protected filterArbitraryProducts(products: Array<Array<ProductGroup>>): Array<Array<ProductGroup>> {
		if (this.isExtensionActive(ExtensionType.Rounding)) {
			const rounding: Extension<RoundingParams> = this.getExtension<RoundingParams>(ExtensionType.Rounding);
			const leftSideRadius: number = Utils.convertPercentToRadius(rounding.params.leftSideRadius, this);
			const rightSideRadius: number = Utils.convertPercentToRadius(rounding.params.rightSideRadius, this);

			if (leftSideRadius) {
				const point: number = Utils.round(this.getBoundingX().min + leftSideRadius);
				products = products.filter((group: Array<ProductGroup>) => group[0].begin.x >= point);
			}

			if (rightSideRadius) {
				const point: number = Utils.round(this.getBoundingX().max - rightSideRadius);
				products = products.filter((group: Array<ProductGroup>) => group[0].begin.x <= point);
			}
		}

		return products;
	}

	protected hasRoundingIntersections(product: ProductGroup, side: 'left' | 'right' | 'both' = 'both'): boolean {
		if (!this.isExtensionActive(ExtensionType.Rounding)) return false;

		const isPointInRect = function (point: Point, rectBegin: Point, side: number): boolean {
			return (
				point.x >= rectBegin.x && point.x <= Utils.round(rectBegin.x + side) &&
				point.y >= rectBegin.y && point.y <= Utils.round(rectBegin.y + side)
			);
		};
		const isPointInCircle = function (point: Point, circleCenter: Point, radius: number): boolean {
			const dx: number = Utils.round(point.x - circleCenter.x);
			const dy: number = Utils.round(point.y - circleCenter.y);
			return Utils.round(dx * dx + dy * dy) <= Utils.round(radius * radius);
		};
		const boundingX: Bounding = this.getBoundingX();
		const boundingZ: Bounding = this.getBoundingZ();
		const rounding: Extension<RoundingParams> = this.getExtension<RoundingParams>(ExtensionType.Rounding);
		const leftRadius: number = Utils.convertPercentToRadius(rounding.params.leftSideRadius, this);
		const rightRadius: number = Utils.convertPercentToRadius(rounding.params.rightSideRadius, this);

		for (let c of product.zCoordinates) {
			if (side == 'left' || side == 'both') {
				const rectPoint1: Point = { x: boundingX.min, y: boundingZ.max - leftRadius };
				const circlePoint1: Point = { x: boundingX.min + leftRadius, y: boundingZ.max - leftRadius };
				if (isPointInRect(c, rectPoint1, leftRadius) && !isPointInCircle(c, circlePoint1, leftRadius)) return true;
			}

			if (side == 'left' || side == 'both') {
				const rectPoint2: Point = { x: boundingX.min, y: boundingZ.min };
				const circlePoint2: Point = { x: boundingX.min + leftRadius, y: boundingZ.min + leftRadius };
				if (isPointInRect(c, rectPoint2, leftRadius) && !isPointInCircle(c, circlePoint2, leftRadius)) return true;
			}

			if (side == 'right' || side == 'both') {
				const rectPoint3: Point = { x: boundingX.max - rightRadius, y: boundingZ.min };
				const circlePoint3: Point = { x: boundingX.max - rightRadius, y: boundingZ.min + rightRadius };
				if (isPointInRect(c, rectPoint3, rightRadius) && !isPointInCircle(c, circlePoint3, rightRadius)) return true;
			}

			if (side == 'right' || side == 'both') {
				const rectPoint4: Point = { x: boundingX.max - rightRadius, y: boundingZ.max - rightRadius };
				const circlePoint4: Point = { x: boundingX.max - rightRadius, y: boundingZ.max - rightRadius };
				if (isPointInRect(c, rectPoint4, rightRadius) && !isPointInCircle(c, circlePoint4, rightRadius)) return true;
			}
		}

		return false;
	}
}